// client/components/Inventar/InventarBearbeiten.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../constants/api";
import { showSuccess, showError } from "../../utils/toast.js";
import InventarBlockFormular from "./InventarBlockFormular";
import { useTheme } from "../../context/ThemeContext.jsx";
import { useConfirm } from "../ui/ConfirmationModal.jsx";
import { useFormDirty } from "../../hooks/useFormDirty.js";
export default function InventarBearbeiten() {
  const {
    id
  } = useParams();
  const artikelId = useMemo(() => Number.parseInt(id, 10), [id]);
  const navigate = useNavigate();
    const confirm = useConfirm();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const mountedRef = useRef(true);
  const { isDirty, syncBaseline } = useFormDirty(form, {
    ready: !loading && form != null
  });
  useEffect(() => {
    const old = document.title;
    document.title = `Inventar bearbeiten – WireTrack`;
    return () => {
      document.title = old;
    };
  }, []);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") navigate(`/inventar/${artikelId}`);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, artikelId]);
  useEffect(() => {
    if (!Number.isFinite(artikelId)) return;
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        const {
          data
        } = await api.get(`/inventar/${artikelId}`, {
          signal: ac.signal
        });
        if (!mountedRef.current) return;
        const payload = data?.data || data || {};
        const next = {
          projekttyp: payload?.projekttyp || "",
          kategorie: payload?.kategorie || "",
          bezeichnung: payload?.bezeichnung || "",
          hersteller: payload?.hersteller || "",
          seriennummer: payload?.seriennummer || "",
          status: payload?.status || "verfügbar",
          standort_typ: payload?.standort_typ || "Lager",
          standort_bez: payload?.standort_bez || "",
          notiz: payload?.notiz || "",
          details: payload?.details || {}
        };
        setForm(next);
        syncBaseline(next);
      } catch (e) {
        if (e?.name !== "CanceledError") {
          console.error(e);
          showError("Inventar konnte nicht geladen werden");
          navigate("/inventar");
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [artikelId, navigate]);
  const handleSubmit = async e => {
    e.preventDefault();
    if (!form || saving) return;
    try {
      setSaving(true);
      await api.put(`/inventar/${artikelId}`, {
        projekttyp: form.projekttyp,
        kategorie: form.kategorie,
        bezeichnung: form.bezeichnung,
        hersteller: form.hersteller,
        seriennummer: form.seriennummer,
        status: form.status,
        standort_typ: form.standort_typ,
        standort_bez: form.standort_bez,
        notiz: form.notiz
      });
      await api.post(`/inventar/${artikelId}/details`, form.details || {});
      showSuccess("Gespeichert");
      navigate(`/inventar/${artikelId}`);
    } catch (e) {
      console.error("Inventar speichern fehlgeschlagen:", e);
      showError(e, {
        fallbackKey: "Konnte nicht gespeichert werden"
      });
    } finally {
      setSaving(false);
    }
  };
  const handleCancel = useCallback(async () => {
    if (isDirty) {
      const {
        ok
      } = await confirm({
        title: "Änderungen verwerfen?",
        message: "Änderungen verwerfen und zurück?",
        variant: "warning",
        confirmLabel: "Verwerfen"
      });
      if (!ok) return;
    }
    navigate(`/inventar/${artikelId}`);
  }, [isDirty, confirm, navigate, artikelId]);
  return <div className={`min-h-dvh w-full overscroll-contain ${"bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white"}`}>
      <div className="mx-auto w-full px-3 sm:px-4 py-4 sm:py-6" style={{
      paddingTop: "max(1rem, env(safe-area-inset-top))",
      paddingBottom: "max(1rem, env(safe-area-inset-bottom))"
    }}>
        <div className="mx-auto max-w-screen-sm md:max-w-3xl">
          <div className={`rounded-2xl border shadow-xl ${"glass border-white/10"}`}>
            <div className="px-4 sm:px-5 py-4 sm:py-5">
              {!form || loading ? <div role="status" aria-live="polite" className="space-y-4 animate-pulse">
                  <div className={`h-5 w-40 rounded ${"bg-white/10"}`} />
                  <div className={`h-10 w-full rounded ${"bg-white/10"}`} />
                  <div className={`h-10 w-5/6 rounded ${"bg-white/10"}`} />
                  <div className={`h-10 w-3/4 rounded ${"bg-white/10"}`} />
                  <div className={`h-32 w-full rounded ${"bg-white/10"}`} />
                </div> : <InventarBlockFormular form={form} setForm={setForm} onSubmit={handleSubmit} onCancel={handleCancel} isEdit isSaving={saving} />}
            </div>
          </div>
          <div className="h-4 md:h-6" />
        </div>
      </div>
    </div>;
}